import { useState, useRef, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";
import LandingPage from "./LandingPage";
import AdminPortal from "./AdminPortal";
import { SaarthLogo, TAGLINE } from "./Logo";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || "";
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || "";
const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

const LANGUAGE_OPTIONS = [
  { value: "Hinglish", label: "Hinglish",          desc: "Hindi + English mix", flag: "🇮🇳" },
  { value: "Hindi",    label: "हिंदी (Hindi)",      desc: "Pure Hindi",          flag: "🇮🇳" },
  { value: "English",  label: "English",            desc: "Pure English",        flag: "🇬🇧" },
  { value: "Marathi",  label: "मराठी (Marathi)",   desc: "Pure Marathi",        flag: "🟠" },
];


// ── Helpers ────────────────────────────────────────────────────────────────────
const LoadingDots = () => (
  <span className="loading-dots">
    <span>.</span><span>.</span><span>.</span>
  </span>
);

const MessageBubble = ({ msg }) => (
  <div className={`message-row ${msg.role}`}>
    {msg.role === "assistant" && (
      <img src="/PrakashSir.jpg" alt="Prakash Sir" className="avatar-small" />
    )}
    <div className={`bubble ${msg.role}`}>
      {msg.loading ? <LoadingDots /> : (
        <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", margin: 0 }}>
          {msg.content}
        </pre>
      )}
    </div>
  </div>
);

// ── INDIAN STATES ──────────────────────────────────────────────────────────────
const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat",
  "Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh",
  "Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan",
  "Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
  "Andaman & Nicobar Islands","Chandigarh","Dadra & Nagar Haveli","Daman & Diu","Delhi",
  "Jammu & Kashmir","Ladakh","Lakshadweep","Puducherry",
];

// ── OTP Auth Page (Login + Signup) ─────────────────────────────────────────────
const OTPAuthPage = ({ onVerified, onGoLanding, initialMode = "login" }) => {
  const [mode, setMode]       = useState(initialMode); // "login" | "signup"
  const [step, setStep]       = useState("form");       // "form" | "otp"
  const [email, setEmail]     = useState("");
  const [otp, setOtp]         = useState(["","","","","",""]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [sf, setSFState]      = useState({
    firstName:"", lastName:"", phone:"", city:"", state:"",
    course:"", language:"Hinglish",
  });
  const otpRefs = useRef([]);
  const setSF = (k, v) => setSFState(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(r => r - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const switchMode = (m) => { setMode(m); setStep("form"); setError(""); setOtp(["","","","","",""]); };

  // ── Login: send OTP (existing users only) ──────────────────────────────────
  const sendOTP = async () => {
    if (!email.trim()) { setError("Please enter your email address."); return; }
    setLoading(true); setError("");
    const { error: err } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { shouldCreateUser: false },
    });
    if (err) {
      if (err.message?.toLowerCase().includes("signups not allowed") || err.message?.toLowerCase().includes("user not found")) {
        setError("Looks like you're a new user! Please sign up with your email ID to continue.");
      } else {
        setError(err.message);
      }
      setLoading(false); return;
    }
    setLoading(false); setStep("otp"); setResendTimer(30);
  };

  // ── Signup: validate form → send OTP (creates user) ───────────────────────
  const submitSignup = async () => {
    if (!sf.firstName.trim())  { setError("Please enter your first name."); return; }
    if (!sf.lastName.trim())   { setError("Please enter your last name."); return; }
    if (!email.trim() || !email.includes("@")) { setError("Please enter a valid email address."); return; }
    if (!sf.phone.trim())      { setError("Please enter your phone number."); return; }
    if (!sf.city.trim())       { setError("Please enter your city."); return; }
    if (!sf.state)             { setError("Please select your state."); return; }
    if (!sf.course)            { setError("Please select your course."); return; }
    setLoading(true); setError("");

    // Check if this email already has an account
    const { error: checkErr } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { shouldCreateUser: false },
    });
    if (!checkErr) {
      setMode("login");
      setStep("otp");
      setResendTimer(30);
      setError("This email is already registered. A sign-in code has been sent — please use it to log in.");
      setLoading(false);
      return;
    }

    const { error: err } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { shouldCreateUser: true },
    });
    if (err) { setError(err.message); setLoading(false); return; }
    setLoading(false); setStep("otp"); setResendTimer(30);
  };

  // ── Verify OTP (login → App routes; signup → saves profile first) ──────────
  const verifyOTP = async () => {
    const token = otp.join("");
    if (token.length < 6) { setError("Please enter the complete 6-digit code."); return; }
    setLoading(true); setError("");

    // Supabase v2 uses Web Locks internally. If the network hangs, the lock is
    // held forever and Promise.race can't break it — only a page reload can.
    let didTimeout = false;
    const failsafe = setTimeout(() => {
      didTimeout = true;
      setLoading(false);
      setError("Connection is slow — reloading so you can try again…");
      setTimeout(() => window.location.reload(), 2500);
    }, 15000);

    let data, err;
    try {
      const result = await supabase.auth.verifyOtp({
        email: email.trim(), token, type: "email",
      });
      data = result.data;
      err  = result.error;
    } catch (e) {
      clearTimeout(failsafe);
      if (!didTimeout) { setError("Something went wrong. Please try again."); setLoading(false); }
      return;
    }

    if (didTimeout) return; // failsafe already fired

    if (err) { clearTimeout(failsafe); setError("Invalid or expired code. Please try again."); setLoading(false); return; }
    if (!data?.user) { clearTimeout(failsafe); setError("Verification failed — please request a new code."); setLoading(false); return; }

    if (mode === "signup") {
      const { error: pErr } = await supabase.from("profiles").upsert({
        id:         data.user.id,
        name:       `${sf.firstName.trim()} ${sf.lastName.trim()}`,
        first_name: sf.firstName.trim(),
        last_name:  sf.lastName.trim(),
        phone:      sf.phone.trim(),
        city:       sf.city.trim(),
        state:      sf.state,
        course:     sf.course,
        language:   sf.language,
      });
      clearTimeout(failsafe);
      if (pErr) { setError(pErr.message); setLoading(false); return; }
      onVerified(data.user, {
        name:     `${sf.firstName.trim()} ${sf.lastName.trim()}`,
        email:    data.user.email,
        course:   sf.course,
        language: sf.language,
        city:     sf.city,
        state:    sf.state,
      });
      return;
    }

    // Login: verifyOtp fired SIGNED_IN internally. The onAuthStateChange handler
    // will call loadProfileAndRoute via setTimeout(0) — AFTER the auth lock releases.
    // Keep failsafe running so a stuck routing attempt still reloads.
    // (clearTimeout is called when the page transitions and this component unmounts)
  };

  const handleOtpKey = (i, e) => {
    if (e.key === "Backspace") {
      if (otp[i]) { const n=[...otp]; n[i]=""; setOtp(n); }
      else if (i > 0) otpRefs.current[i-1]?.focus();
    }
  };
  const handleOtpChange = (i, val) => {
    const d = val.replace(/\D/,"").slice(-1);
    const n = [...otp]; n[i] = d; setOtp(n);
    if (d && i < 5) otpRefs.current[i+1]?.focus();
  };
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const p = e.clipboardData.getData("text").replace(/\D/g,"").slice(0,6);
    if (!p) return;
    const n = [...otp]; p.split("").forEach((d,i) => { n[i]=d; }); setOtp(n);
    otpRefs.current[Math.min(p.length,5)]?.focus();
  };

  const BrandHeader = () => (
    <div className="brand-lockup">
      <SaarthLogo height={48} />
      <div>
        <div className="brand-name">SaarthAI</div>
        <div className="brand-tagline">{TAGLINE}</div>
      </div>
    </div>
  );

  // ── Login Form ──────────────────────────────────────────────────────────────
  if (step === "form" && mode === "login") return (
    <div className="auth-page">
      <div className="auth-card">
        <button onClick={onGoLanding} className="back-btn">← Back</button>
        <BrandHeader />
        <h2 className="auth-heading">Welcome Back 👋</h2>
        <p className="auth-sub">Enter your email — we'll send a 6-digit OTP. No password needed!</p>
        {error && (
          <div className="error-box">
            {error}
            {error.toLowerCase().includes("new user") && (
              <div style={{ marginTop:8 }}>
                <span style={{ color:"#1B3A6B", fontWeight:700, cursor:"pointer", textDecoration:"underline" }}
                  onClick={() => switchMode("signup")}>
                  Sign up here →
                </span>
              </div>
            )}
          </div>
        )}
        <div className="field-group">
          <label>Email Address</label>
          <input type="email" placeholder="your@email.com" value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === "Enter" && sendOTP()} autoFocus />
        </div>
        <button className="cta-btn" onClick={sendOTP} disabled={loading}>
          {loading ? "Sending code..." : "Send OTP →"}
        </button>
        <p className="auth-switch">
          New user?{" "}
          <span onClick={() => switchMode("signup")}>Sign up for free</span>
        </p>
      </div>
    </div>
  );

  // ── Signup Form ─────────────────────────────────────────────────────────────
  if (step === "form" && mode === "signup") return (
    <div className="auth-page">
      <div className="auth-card" style={{ maxWidth:520 }}>
        <button onClick={onGoLanding} className="back-btn">← Back</button>
        <BrandHeader />
        <h2 className="auth-heading">Sign Up for Free 🎉</h2>
        <p className="auth-sub">Create your account and start learning with Prakash Sir!</p>
        {error && <div className="error-box">{error}</div>}

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <div className="field-group">
            <label>First Name</label>
            <input type="text" placeholder="e.g. Rahul" value={sf.firstName}
              onChange={e => setSF("firstName", e.target.value)} autoFocus />
          </div>
          <div className="field-group">
            <label>Last Name</label>
            <input type="text" placeholder="e.g. Sharma" value={sf.lastName}
              onChange={e => setSF("lastName", e.target.value)} />
          </div>
        </div>

        <div className="field-group">
          <label>Email Address</label>
          <input type="email" placeholder="your@email.com" value={email}
            onChange={e => setEmail(e.target.value)} />
        </div>

        <div className="field-group">
          <label>Phone Number</label>
          <input type="tel" placeholder="10-digit mobile number" value={sf.phone}
            onChange={e => setSF("phone", e.target.value)} />
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <div className="field-group">
            <label>City</label>
            <input type="text" placeholder="e.g. Pune" value={sf.city}
              onChange={e => setSF("city", e.target.value)} />
          </div>
          <div className="field-group">
            <label>State</label>
            <select value={sf.state} onChange={e => setSF("state", e.target.value)}>
              <option value="">-- Select --</option>
              {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div className="field-group">
          <label>Select Course</label>
          <select value={sf.course} onChange={e => setSF("course", e.target.value)}>
            <option value="">-- Select your course --</option>
            <option value="DMLT">DMLT – Diploma in Medical Lab Technology</option>
            <option value="BMLT">BMLT – Bachelor in Medical Lab Technology</option>
            <option value="Nursing">B.Sc Nursing / GNM</option>
            <option value="BPharma">B.Pharma / D.Pharma</option>
          </select>
        </div>

        <div className="field-group">
          <label>Preferred Language</label>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginTop:4 }}>
            {LANGUAGE_OPTIONS.map(l => (
              <button key={l.value} type="button" onClick={() => setSF("language", l.value)}
                style={{
                  padding:"11px 10px",
                  border:`2px solid ${sf.language===l.value?"var(--navy,#1B3A6B)":"var(--border,#D4DCF0)"}`,
                  borderRadius:12,
                  background: sf.language===l.value?"var(--navy-pale,#EEF3FF)":"var(--surface,#F5F8FF)",
                  color: sf.language===l.value?"var(--navy,#1B3A6B)":"var(--muted,#6B7FA3)",
                  fontWeight: sf.language===l.value?700:500,
                  fontSize:13, cursor:"pointer", transition:"all 0.15s",
                  textAlign:"center", lineHeight:1.4,
                }}>
                <div style={{ fontSize:16, marginBottom:3 }}>{l.flag}</div>
                <div>{l.label}</div>
                <div style={{ fontSize:11, opacity:0.7, marginTop:2 }}>{l.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <button className="cta-btn" onClick={submitSignup} disabled={loading}>
          {loading ? "Creating account..." : "Create Account & Send OTP →"}
        </button>
        <p className="auth-switch">
          Already have an account?{" "}
          <span onClick={() => switchMode("login")}>Log in</span>
        </p>
      </div>
    </div>
  );

  // ── OTP Verification (shared for login + signup) ────────────────────────────
  return (
    <div className="auth-page">
      <div className="auth-card">
        <button onClick={() => { setStep("form"); setOtp(["","","","","",""]); setError(""); }}
          className="back-btn">← {mode === "signup" ? "Back to form" : "Change email"}</button>
        <BrandHeader />
        <h2 className="auth-heading">Check Your Email 📧</h2>
        <p className="auth-sub">We sent a 6-digit code to <strong>{email}</strong></p>
        {error && <div className="error-box">{error}</div>}
        <div style={{ display:"flex", gap:10, justifyContent:"center", margin:"24px 0" }}>
          {otp.map((d,i) => (
            <input key={i} ref={el => otpRefs.current[i]=el}
              type="text" inputMode="numeric" maxLength={1} value={d}
              onChange={e => handleOtpChange(i, e.target.value)}
              onKeyDown={e => handleOtpKey(i, e)}
              onPaste={i===0 ? handleOtpPaste : undefined}
              style={{
                width:48, height:56, textAlign:"center",
                fontSize:22, fontWeight:700,
                border:`2px solid ${d?"var(--navy)":"var(--border)"}`,
                borderRadius:12, outline:"none",
                background: d?"var(--navy-pale,#EEF3FF)":"var(--surface,#F5F8FF)",
                color:"var(--navy,#1B3A6B)", transition:"border-color 0.15s",
              }} />
          ))}
        </div>
        <button className="cta-btn" onClick={verifyOTP} disabled={loading || otp.join("").length < 6}>
          {loading ? "Verifying..." : "Verify Code ✓"}
        </button>
        <div className="auth-switch">
          Didn't receive it?{" "}
          {resendTimer > 0
            ? <span style={{color:"var(--muted)"}}>Resend in {resendTimer}s</span>
            : <span onClick={mode === "signup" ? submitSignup : sendOTP}>Resend OTP</span>}
        </div>
      </div>
    </div>
  );
};

// ── Profile Setup Page (new users) ────────────────────────────────────────────
const ProfileSetupPage = ({ supabaseUser, onComplete }) => {
  const [form, setForm] = useState({
    firstName:"", lastName:"", phone:"", city:"", state:"",
    course:"", language:"Hinglish",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.firstName.trim()) { setError("Please enter your first name."); return; }
    if (!form.lastName.trim())  { setError("Please enter your last name."); return; }
    if (!form.phone.trim())     { setError("Please enter your phone number."); return; }
    if (!form.city.trim())      { setError("Please enter your city."); return; }
    if (!form.state)            { setError("Please select your state."); return; }
    if (!form.course)           { setError("Please select your course."); return; }
    setLoading(true); setError("");
    const { error: err } = await supabase.from("profiles").upsert({
      id:         supabaseUser.id,
      name:       `${form.firstName.trim()} ${form.lastName.trim()}`,
      first_name: form.firstName.trim(),
      last_name:  form.lastName.trim(),
      phone:      form.phone.trim(),
      city:       form.city.trim(),
      state:      form.state,
      course:     form.course,
      language:   form.language,
    });
    if (err) { setError(err.message); setLoading(false); return; }
    setLoading(false);
    onComplete({
      name:     `${form.firstName.trim()} ${form.lastName.trim()}`,
      email:    supabaseUser.email,
      course:   form.course,
      language: form.language,
      city:     form.city,
      state:    form.state,
    });
  };

  return (
    <div className="auth-page">
      <div className="auth-card" style={{ maxWidth: 500 }}>
        <div className="brand-lockup">
          <SaarthLogo height={44} />
          <div>
            <div className="brand-name">SaarthAI</div>
            <div className="brand-tagline">{TAGLINE}</div>
          </div>
        </div>
        <h2 className="auth-heading">Complete Your Profile 🙏</h2>
        <p className="auth-sub">Help Prakash Sir personalise your learning experience.</p>
        {error && <div className="error-box">{error}</div>}

        {/* Name row */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <div className="field-group">
            <label>First Name</label>
            <input type="text" placeholder="e.g. Rahul" value={form.firstName}
              onChange={e => set("firstName", e.target.value)} autoFocus />
          </div>
          <div className="field-group">
            <label>Last Name</label>
            <input type="text" placeholder="e.g. Sharma" value={form.lastName}
              onChange={e => set("lastName", e.target.value)} />
          </div>
        </div>

        <div className="field-group">
          <label>Phone Number</label>
          <input type="tel" placeholder="10-digit mobile number" value={form.phone}
            onChange={e => set("phone", e.target.value)} />
        </div>

        {/* City + State row */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <div className="field-group">
            <label>City</label>
            <input type="text" placeholder="e.g. Pune" value={form.city}
              onChange={e => set("city", e.target.value)} />
          </div>
          <div className="field-group">
            <label>State</label>
            <select value={form.state} onChange={e => set("state", e.target.value)}>
              <option value="">-- Select --</option>
              {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div className="field-group">
          <label>Select Course</label>
          <select value={form.course} onChange={e => set("course", e.target.value)}>
            <option value="">-- Select your course --</option>
            <option value="DMLT">DMLT – Diploma in Medical Lab Technology</option>
            <option value="BMLT">BMLT – Bachelor in Medical Lab Technology</option>
            <option value="Nursing">B.Sc Nursing / GNM</option>
            <option value="BPharma">B.Pharma / D.Pharma</option>
          </select>
        </div>

        <div className="field-group">
          <label>Preferred Language</label>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginTop:4 }}>
            {LANGUAGE_OPTIONS.map(l => (
              <button key={l.value} type="button" onClick={() => set("language", l.value)}
                style={{
                  padding:"11px 10px",
                  border:`2px solid ${form.language===l.value?"var(--navy,#1B3A6B)":"var(--border,#D4DCF0)"}`,
                  borderRadius:12,
                  background: form.language===l.value?"var(--navy-pale,#EEF3FF)":"var(--surface,#F5F8FF)",
                  color: form.language===l.value?"var(--navy,#1B3A6B)":"var(--muted,#6B7FA3)",
                  fontWeight: form.language===l.value?700:500,
                  fontSize:13, cursor:"pointer", transition:"all 0.15s",
                  textAlign:"center", lineHeight:1.4,
                }}>
                <div style={{ fontSize:16, marginBottom:3 }}>{l.flag}</div>
                <div>{l.label}</div>
                <div style={{ fontSize:11, opacity:0.7, marginTop:2 }}>{l.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <button className="cta-btn" onClick={save} disabled={loading}>
          {loading ? "Setting up your account..." : "Start Learning with Prakash Sir 🎉"}
        </button>
      </div>
    </div>
  );
};

// ── Home Dashboard (after login) ───────────────────────────────────────────────
const COURSE_ICONS = { DMLT:"🔬", BMLT:"🧪", Nursing:"🏥", BPharma:"💊" };
const COURSE_FULL  = {
  DMLT:    "Diploma in Medical Lab Technology",
  BMLT:    "Bachelor in Medical Lab Technology",
  Nursing: "B.Sc Nursing / GNM",
  BPharma: "B.Pharma / D.Pharma",
};
const HomePage = ({ user, onStartChat, onLogout }) => {
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good Morning" : hour < 17 ? "Good Afternoon" : "Good Evening";
  const firstName = user.name?.split(" ")[0] || user.name || "User";

  return (
    <div style={{
      minHeight:"100vh", background:"var(--surface,#F5F8FF)",
      fontFamily:"'Noto Sans',sans-serif",
    }}>
      {/* Top Nav */}
      <nav style={{
        background:"var(--deep,#0D1F3C)", padding:"0 32px",
        display:"flex", alignItems:"center", justifyContent:"space-between",
        height:64, boxShadow:"0 2px 12px rgba(0,0,0,0.2)",
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <SaarthLogo height={36} variant="mono" />
          <span style={{ color:"white", fontFamily:"'Baloo 2',cursive", fontSize:20, fontWeight:800 }}>SaarthAI</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <div style={{
            background:"rgba(255,255,255,0.1)", borderRadius:24,
            padding:"6px 16px", display:"flex", alignItems:"center", gap:8,
          }}>
            <div style={{
              width:30, height:30, borderRadius:"50%",
              background:"linear-gradient(135deg,var(--navy,#1B3A6B),var(--navy-light,#2D5AA0))",
              display:"flex", alignItems:"center", justifyContent:"center",
              color:"white", fontWeight:700, fontSize:13,
            }}>{(firstName || "U")[0].toUpperCase()}</div>
            <span style={{ color:"white", fontSize:14, fontWeight:600 }}>{firstName}</span>
          </div>
          <button onClick={onLogout} style={{
            background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.15)",
            color:"rgba(255,255,255,0.6)", borderRadius:8, padding:"6px 14px",
            fontSize:13, cursor:"pointer",
          }}>Logout</button>
        </div>
      </nav>

      {/* Main Content */}
      <div style={{ maxWidth:900, margin:"0 auto", padding:"40px 24px" }}>

        {/* Welcome Banner */}
        <div style={{
          background:"linear-gradient(135deg,var(--navy,#1B3A6B) 0%,var(--navy-light,#2D5AA0) 100%)",
          borderRadius:20, padding:"32px 36px", marginBottom:28,
          display:"flex", alignItems:"center", justifyContent:"space-between",
          boxShadow:"0 8px 32px rgba(27,58,107,0.3)",
        }}>
          <div>
            <p style={{ color:"rgba(255,255,255,0.7)", fontSize:14, marginBottom:6 }}>
              {greeting} 👋
            </p>
            <h1 style={{
              color:"white", fontFamily:"'Baloo 2',cursive",
              fontSize:28, fontWeight:800, marginBottom:8,
            }}>Welcome back, {firstName}!</h1>
            <p style={{ color:"rgba(255,255,255,0.75)", fontSize:15 }}>
              {COURSE_ICONS[user.course] || "📚"} {COURSE_FULL[user.course] || "Your Course"} &nbsp;·&nbsp; {user.language || "Hinglish"}
            </p>
          </div>
          <img src="/PrakashSir.jpg" alt="Prakash Sir"
            style={{
              width:90, height:90, borderRadius:"50%",
              border:"3px solid rgba(255,255,255,0.3)",
              objectFit:"cover", flexShrink:0,
              display:"block",
            }} />
        </div>

        {/* Chat CTA Card */}
        <div style={{
          background:"white", borderRadius:20, padding:"28px 32px", marginBottom:24,
          border:"1px solid var(--border,#D4DCF0)",
          boxShadow:"0 4px 20px rgba(27,58,107,0.08)",
          display:"flex", alignItems:"center", justifyContent:"space-between", gap:24,
        }}>
          <div>
            <h2 style={{
              fontFamily:"'Baloo 2',cursive", fontSize:22, fontWeight:700,
              color:"var(--navy,#1B3A6B)", marginBottom:8,
            }}>
              Chat with Prakash Sir 💬
            </h2>
            <p style={{ color:"var(--muted,#6B7FA3)", fontSize:14, lineHeight:1.6 }}>
              Your AI teacher is ready! Ask about {user.course || "your course"} topics,
              get mind maps, mnemonics and step-by-step explanations in {user.language || "Hinglish"}.
            </p>
          </div>
          <button onClick={onStartChat} style={{
            background:"linear-gradient(135deg,var(--navy,#1B3A6B),var(--navy-light,#2D5AA0))",
            color:"white", border:"none", borderRadius:14, padding:"14px 28px",
            fontFamily:"'Baloo 2',cursive", fontSize:16, fontWeight:700,
            cursor:"pointer", whiteSpace:"nowrap", flexShrink:0,
            boxShadow:"0 4px 20px rgba(27,58,107,0.35)",
            transition:"transform 0.15s, box-shadow 0.15s",
          }}
            onMouseEnter={e => { e.target.style.transform="translateY(-2px)"; e.target.style.boxShadow="0 8px 28px rgba(27,58,107,0.45)"; }}
            onMouseLeave={e => { e.target.style.transform=""; e.target.style.boxShadow="0 4px 20px rgba(27,58,107,0.35)"; }}
          >
            Start Chatting →
          </button>
        </div>

        {/* Info Cards */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
          <div style={{
            background:"white", borderRadius:16, padding:"22px 24px",
            border:"1px solid var(--border,#D4DCF0)",
            boxShadow:"0 2px 12px rgba(27,58,107,0.06)",
          }}>
            <div style={{ fontSize:28, marginBottom:10 }}>{COURSE_ICONS[user.course] || "📚"}</div>
            <h3 style={{ fontFamily:"'Baloo 2',cursive", fontSize:16, fontWeight:700, color:"var(--navy,#1B3A6B)", marginBottom:6 }}>
              Your Course
            </h3>
            <p style={{ color:"var(--muted,#6B7FA3)", fontSize:14 }}>{COURSE_FULL[user.course] || "Not set"}</p>
            <p style={{ color:"#A0AECB", fontSize:12, marginTop:6 }}>
              🔒 Locked · To change, contact admin
            </p>
          </div>
          <div style={{
            background:"white", borderRadius:16, padding:"22px 24px",
            border:"1px solid var(--border,#D4DCF0)",
            boxShadow:"0 2px 12px rgba(27,58,107,0.06)",
          }}>
            <div style={{ fontSize:28, marginBottom:10 }}>🗣️</div>
            <h3 style={{ fontFamily:"'Baloo 2',cursive", fontSize:16, fontWeight:700, color:"var(--navy,#1B3A6B)", marginBottom:6 }}>
              Learning Language
            </h3>
            <p style={{ color:"var(--muted,#6B7FA3)", fontSize:14 }}>
              Prakash Sir will explain in <strong>{user.language || "Hinglish"}</strong>
            </p>
          </div>
          <div style={{
            background:"white", borderRadius:16, padding:"22px 24px",
            border:"1px solid var(--border,#D4DCF0)",
            boxShadow:"0 2px 12px rgba(27,58,107,0.06)",
          }}>
            <div style={{ fontSize:28, marginBottom:10 }}>📍</div>
            <h3 style={{ fontFamily:"'Baloo 2',cursive", fontSize:16, fontWeight:700, color:"var(--navy,#1B3A6B)", marginBottom:6 }}>
              Location
            </h3>
            <p style={{ color:"var(--muted,#6B7FA3)", fontSize:14 }}>{user.city ? `${user.city}, ${user.state}` : "India"}</p>
          </div>
          <div style={{
            background:"linear-gradient(135deg,#FFF8E7,#FFF3CD)", borderRadius:16, padding:"22px 24px",
            border:"1px solid #FFE082",
            boxShadow:"0 2px 12px rgba(255,193,7,0.12)",
          }}>
            <div style={{ fontSize:28, marginBottom:10 }}>⭐</div>
            <h3 style={{ fontFamily:"'Baloo 2',cursive", fontSize:16, fontWeight:700, color:"#B8860B", marginBottom:6 }}>
              Study Tip
            </h3>
            <p style={{ color:"#8B6914", fontSize:13, lineHeight:1.5 }}>
              Consistency beats intensity! Study 30 mins daily with Prakash Sir for best results.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Welcome messages ───────────────────────────────────────────────────────────
const WELCOME_MESSAGES = {
  Hinglish: (name, course) => `Arre ${name}! Kaise ho? 😊\n\nMain hoon Prakash Sir — tera personal AI study buddy!\n\n${course ? `${course} ki padhai kar rahe ho? Bilkul sahi choice hai! 💪\n\nAaj kaunsa topic padhna hai? Main sirf ${course} ke syllabus mein se samjhaunga — step by step, mind maps ke saath! ⭐` : "Koi bhi subject ho — main hoon na! Aaj kya padhna hai?"}`,
  Hindi:    (name, course) => `अरे ${name}! कैसे हो? 😊\n\nमैं हूँ प्रकाश सर — तुम्हारा निजी AI अध्ययन साथी!\n\n${course ? `${course} की पढ़ाई कर रहे हो? बिल्कुल सही चुनाव! 💪\n\nआज कौनसा टॉपिक पढ़ना है? मैं सिर्फ ${course} के सिलेबस में से समझाऊंगा! ⭐` : "कोई भी विषय हो — मैं हूँ ना!"}`,
  English:  (name, course) => `Hello ${name}! How are you? 😊\n\nI'm Prakash Sir — your personal AI study buddy!\n\n${course ? `You're studying ${course}? Excellent choice! 💪\n\nWhat would you like to study today? I'll guide you through ${course} topics with clear explanations and mind maps! ⭐` : "Whatever subject you need help with — I'm here!"}`,
  Marathi:  (name, course) => `अरे ${name}! कसे आहात? 😊\n\nमी आहे प्रकाश सर — तुमचा वैयक्तिक AI अभ्यास मित्र!\n\n${course ? `${course} चा अभ्यास करत आहात? अगदी योग्य निवड! 💪\n\nआज कोणता टॉपिक शिकायचा आहे? मी फक्त ${course} च्या अभ्यासक्रमातून समजावून सांगेन! ⭐` : "कोणताही विषय असो — मी आहे ना!"}`,
};

// ── Chat Page ──────────────────────────────────────────────────────────────────
const ChatPage = ({ user, onLogout, onGoHome }) => {
  const lang    = user.language || "Hinglish";
  const course  = user.course   || "";
  const welcomeFn = WELCOME_MESSAGES[lang] || WELCOME_MESSAGES.Hinglish;

  const [messages, setMessages] = useState([
    { role:"assistant", content: welcomeFn(user.name, course), id: 1 }
  ]);
  const [input, setInput]       = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages]);

  const handleLogout = () => onLogout();

  // Course-specific quick prompts
  const quickPrompts = {
    DMLT:    ["Explain Haematology basics", "What is Blood Banking?", "CBC test — step by step", "Microbiology culture methods"],
    BMLT:    ["Explain Immunology basics", "What is Molecular Biology?", "Lab Quality Control methods", "Advanced Haematology topics"],
    Nursing: ["Explain Vital Signs", "Nursing care for Diabetes patient", "Anatomy of the Heart", "What is Community Health Nursing?"],
    BPharma: ["Explain Pharmacokinetics", "What is Pharmaceutical Chemistry?", "Drug interactions to know", "Pharmacognosy basics"],
    "":      ["What is Haematology?", "Explain Blood Groups", "What are Vital Signs?", "Easy Pharmacology tips"],
  };
  const prompts = quickPrompts[course] || quickPrompts[""];

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;
    const userMsg    = { role:"user",      content: input.trim(), id: Date.now() };
    const loadingMsg = { role:"assistant", content:"", loading:true, id: Date.now()+1 };
    setMessages(prev => [...prev, userMsg, loadingMsg]);
    setInput("");
    setIsLoading(true);

    try {
      // RAG-lite: fetch relevant course material
      let contextBlock = "";
      if (course) {
        const { data: materials } = await supabase
          .from("course_materials")
          .select("title, content_text")
          .eq("course", course)
          .not("content_text", "is", null)
          .limit(3);
        if (materials?.length) {
          contextBlock = "\n\nRelevant notes:\n" +
            materials.map(m => `[${m.title}]:\n${m.content_text?.slice(0,400)}`).join("\n\n");
        }
      }

      const history = messages.map(m => ({ role:m.role, content:m.content }));
      const body = JSON.stringify({
        message:              userMsg.content + (contextBlock ? `\n\n[Course notes context:${contextBlock}]` : ""),
        student_name:         user.name || "",
        subject:              course || "general",
        preferred_language:   lang || "Hinglish",
        conversation_history: history,
      });

      const { data: { session } } = await supabase.auth.getSession();
      const headers = {
        "Content-Type": "application/json",
        "apikey": SUPABASE_KEY,
        ...(session?.access_token ? { "Authorization": `Bearer ${session.access_token}` } : {}),
      };
      const fnUrl = `${SUPABASE_URL}/functions/v1/chat-with-prakash`;

      // Attempt fetch; retry once on 502 (cold-start) after a short delay
      let res = await fetch(fnUrl, { method:"POST", headers, body });
      if (res.status === 502 || res.status === 503) {
        await new Promise(r => setTimeout(r, 1500));
        res = await fetch(fnUrl, { method:"POST", headers, body });
      }

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }

      const data = await res.json();
      if (data.error) throw new Error(data.error);

      const reply = data.text || "Kuch problem aa gayi. Please try again! 🙏";
      setMessages(prev => prev.map(m =>
        m.loading ? { ...m, content: reply, loading: false } : m
      ));
    } catch (e) {
      console.error("Chat error:", e);
      setMessages(prev => prev.map(m =>
        m.loading ? { ...m, content: "Network issue! Please check your connection and try again. 🙏", loading: false } : m
      ));
    }
    setIsLoading(false);
    inputRef.current?.focus();
  };

  return (
    <div className="chat-app">
      <div className={`sidebar ${sidebarOpen?"open":""}`}>
        <div className="sidebar-header">
          <SaarthLogo height={32} variant="mono" />
          <span>SaarthAI</span>
        </div>
        <div className="sidebar-section">
          <div className="sidebar-label">{course ? `${course} Topics` : "Quick Questions"}</div>
          {prompts.map((p,i) => (
            <button key={i} className="quick-btn"
              onClick={() => { setInput(p); setSidebarOpen(false); inputRef.current?.focus(); }}>
              {p}
            </button>
          ))}
        </div>
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">{user.name[0].toUpperCase()}</div>
            <div>
              <div className="user-name">{user.name}</div>
              <div className="user-course">{user.course || "Student"} · {lang}</div>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout}>Logout</button>
        </div>
      </div>

      <div className="chat-main">
        <div className="chat-topbar">
          <button className="menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
          <div className="prakash-identity">
            <div className="online-dot" />
            <span><strong>Prakash Sir</strong> — AI Tutor</span>
          </div>
          {course && <div className="subject-badge">{course}</div>}
          <div className="lang-badge">{lang}</div>
          <button onClick={onGoHome} style={{
            marginLeft:"auto", background:"none", border:"1px solid var(--border)",
            color:"var(--muted)", borderRadius:8, padding:"5px 12px",
            fontSize:13, cursor:"pointer", fontFamily:"'Noto Sans',sans-serif",
          }}>← Home</button>
        </div>

        <div className="messages-area">
          {messages.map(msg => <MessageBubble key={msg.id} msg={msg} />)}
          <div ref={bottomRef} />
        </div>

        <div className="input-area">
          <input ref={inputRef} value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key==="Enter" && !e.shiftKey && sendMessage()}
            placeholder={`Ask Prakash Sir about ${course || "your course"}… 🙏`}
            disabled={isLoading} />
          <button onClick={sendMessage} disabled={isLoading || !input.trim()} className="send-btn">
            {isLoading ? "⏳" : "➤"}
          </button>
        </div>
      </div>

      {sidebarOpen && <div className="overlay" onClick={() => setSidebarOpen(false)} />}
    </div>
  );
};

// ── Root App ───────────────────────────────────────────────────────────────────
export default function App() {
  // page: "landing" | "auth" | "profile-setup" | "chat" | "admin"
  const [page, setPage]           = useState("landing");
  const [authMode, setAuthMode]   = useState("login"); // "login" | "signup"
  const [user, setUser]           = useState(null);
  const [supabaseUser, setSupabaseUser] = useState(null);
  const [checking, setChecking]   = useState(true);

  const goLogin  = () => { setAuthMode("login");  setPage("auth"); };
  const goSignup = () => { setAuthMode("signup"); setPage("auth"); };

  const loadProfileAndRoute = async (sbUser) => {
    try {
      const timeout = new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), 8000));
      const query   = supabase
        .from("profiles")
        .select("name, first_name, last_name, course, language, city, state")
        .eq("id", sbUser.id)
        .single();
      const { data: profile } = await Promise.race([query, timeout]);

      if (profile?.name) {
        setUser({
          name:     profile.name,
          email:    sbUser.email,
          course:   profile.course   || "",
          language: profile.language || "Hinglish",
          city:     profile.city     || "",
          state:    profile.state    || "",
        });
        setPage("home");
      } else {
        setSupabaseUser(sbUser);
        setPage("profile-setup");
      }
    } catch {
      setSupabaseUser(sbUser);
      setPage("profile-setup");
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => setChecking(false), 5000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "INITIAL_SESSION") {
        clearTimeout(timeout);
        if (session) await loadProfileAndRoute(session.user);
        setChecking(false);
      } else if (event === "SIGNED_IN") {
        // Defer so loadProfileAndRoute runs after verifyOtp releases the auth lock.
        // Calling supabase directly inside this callback (while lock is held) → deadlock.
        if (session?.user) setTimeout(() => loadProfileAndRoute(session.user), 0);
      } else if (event === "SIGNED_OUT") {
        setUser(null);
        setPage("landing");
      }
    });
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line

  if (checking) {
    return (
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#F5F8FF" }}>
        <div style={{ color:"#1B3A6B", fontFamily:"sans-serif", fontSize:16 }}>Loading…</div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@400;500;600;700;800&family=Noto+Sans:wght@400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        :root {
          --navy: #1B3A6B; --navy-light: #2D5AA0;
          --navy-glow: rgba(27,58,107,0.15);
          --deep: #0D1F3C; --surface: #F5F8FF;
          --card: #FFFFFF; --text: #0D1F3C;
          --muted: #6B7FA3; --border: #D4DCF0;
          --green: #2ECC71; --user-bubble: #1B3A6B;
        }
        body { font-family:'Noto Sans',sans-serif; background:var(--surface); color:var(--text); min-height:100vh; }

        /* ── AUTH ── */
        .auth-page {
          min-height:100vh; display:flex; align-items:center; justify-content:center;
          background: radial-gradient(ellipse at 20% 20%,rgba(27,58,107,0.1) 0%,transparent 60%),
                      radial-gradient(ellipse at 80% 80%,rgba(45,90,160,0.07) 0%,transparent 50%),
                      var(--surface);
          padding:24px;
        }
        .auth-card {
          background:white; border-radius:24px; padding:40px 36px;
          width:100%; max-width:480px;
          box-shadow:0 4px 6px rgba(27,58,107,0.06),0 20px 60px rgba(27,58,107,0.12),0 0 0 1px var(--border);
          animation:slideUp 0.4s ease;
        }
        @keyframes slideUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        .back-btn { background:none;border:none;color:#6B7FA3;font-size:14px;cursor:pointer;margin-bottom:16px;padding:0;display:flex;align-items:center;gap:6px; }
        .back-btn:hover { color:var(--navy); }
        .brand-lockup { display:flex;align-items:center;gap:14px;margin-bottom:28px; }
        .brand-name { font-family:'Baloo 2',cursive;font-size:22px;font-weight:800;color:var(--navy);line-height:1; }
        .brand-tagline { font-size:12px;color:var(--muted);margin-top:3px; }
        .auth-heading { font-family:'Baloo 2',cursive;font-size:24px;font-weight:700;color:var(--text);margin-bottom:4px; }
        .auth-sub { color:var(--muted);font-size:14px;margin-bottom:24px;line-height:1.6; }
        .error-box { background:#FFF0F0;border:1px solid #FFCDD2;color:#C0392B;border-radius:10px;padding:10px 14px;font-size:13px;margin-bottom:16px; }
        .field-group { margin-bottom:16px; }
        .field-group label { display:block;font-size:12px;font-weight:600;color:var(--muted);margin-bottom:5px;text-transform:uppercase;letter-spacing:0.5px; }
        .field-group input, .field-group select {
          width:100%;padding:12px 14px;border:2px solid var(--border);border-radius:10px;
          font-size:14px;font-family:'Noto Sans',sans-serif;color:var(--text);
          background:var(--surface);transition:border-color 0.2s,box-shadow 0.2s;outline:none;appearance:none;
        }
        .field-group input:focus,.field-group select:focus { border-color:var(--navy);box-shadow:0 0 0 3px var(--navy-glow);background:white; }
        .cta-btn {
          width:100%;padding:14px;background:linear-gradient(135deg,var(--navy),var(--navy-light));
          color:white;border:none;border-radius:12px;font-size:15px;font-weight:700;
          font-family:'Baloo 2',cursive;cursor:pointer;transition:transform 0.15s,box-shadow 0.15s;
          box-shadow:0 4px 20px rgba(27,58,107,0.35);margin-bottom:16px;
        }
        .cta-btn:hover:not(:disabled) { transform:translateY(-2px);box-shadow:0 8px 28px rgba(27,58,107,0.45); }
        .cta-btn:disabled { opacity:0.6;cursor:not-allowed; }
        .auth-switch { text-align:center;font-size:14px;color:var(--muted); }
        .auth-switch span { color:var(--navy);font-weight:700;cursor:pointer; }
        .auth-switch span:hover { text-decoration:underline; }

        /* ── CHAT APP ── */
        .chat-app { display:flex;height:100vh;overflow:hidden;position:relative; }
        .sidebar { width:270px;background:var(--deep);display:flex;flex-direction:column;padding:20px 16px;gap:20px;transition:transform 0.3s ease;z-index:100;flex-shrink:0; }
        @media(max-width:768px){.sidebar{position:fixed;top:0;left:0;height:100%;transform:translateX(-100%)}.sidebar.open{transform:translateX(0)}}
        .sidebar-header { display:flex;align-items:center;gap:12px;font-family:'Baloo 2',cursive;font-size:18px;font-weight:700;color:white;padding-bottom:16px;border-bottom:1px solid rgba(255,255,255,0.08); }
        .sidebar-section { flex:1; }
        .sidebar-label { font-size:11px;font-weight:600;color:rgba(255,255,255,0.35);text-transform:uppercase;letter-spacing:1px;margin-bottom:10px; }
        .quick-btn { display:block;width:100%;text-align:left;background:rgba(27,58,107,0.25);border:1px solid rgba(45,90,160,0.3);border-radius:10px;padding:10px 12px;color:rgba(255,255,255,0.75);font-size:13px;font-family:'Noto Sans',sans-serif;cursor:pointer;margin-bottom:8px;transition:background 0.2s,color 0.2s; }
        .quick-btn:hover { background:rgba(45,90,160,0.45);color:white; }
        .sidebar-footer { border-top:1px solid rgba(255,255,255,0.08);padding-top:16px; }
        .user-info { display:flex;align-items:center;gap:10px;margin-bottom:12px; }
        .user-avatar { width:36px;height:36px;background:linear-gradient(135deg,var(--navy),var(--navy-light));border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;color:white;font-size:14px;flex-shrink:0; }
        .user-name { color:white;font-size:13px;font-weight:600; }
        .user-course { color:rgba(255,255,255,0.4);font-size:11px; }
        .logout-btn { width:100%;background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.1);color:rgba(255,255,255,0.5);border-radius:8px;padding:8px;font-size:13px;cursor:pointer;font-family:'Noto Sans',sans-serif;transition:all 0.2s; }
        .logout-btn:hover { background:rgba(255,60,60,0.15);color:#ff7070;border-color:rgba(255,60,60,0.2); }
        .chat-main { flex:1;display:flex;flex-direction:column;overflow:hidden;background:var(--surface); }
        .chat-topbar { display:flex;align-items:center;gap:12px;padding:14px 20px;background:white;border-bottom:1px solid var(--border);box-shadow:0 1px 8px rgba(0,0,0,0.04); }
        .menu-btn { background:none;border:none;font-size:20px;cursor:pointer;color:var(--muted);padding:4px 8px;border-radius:8px;display:none; }
        @media(max-width:768px){.menu-btn{display:block}}
        .prakash-identity { display:flex;align-items:center;gap:8px;font-size:15px;color:var(--text);flex:1; }
        .online-dot { width:9px;height:9px;background:var(--green);border-radius:50%;box-shadow:0 0 0 2px rgba(46,204,113,0.25);animation:pulse 2s infinite; }
        @keyframes pulse { 0%,100%{box-shadow:0 0 0 2px rgba(46,204,113,0.25)}50%{box-shadow:0 0 0 5px rgba(46,204,113,0.08)} }
        .subject-badge { background:var(--navy-glow);color:var(--navy);font-size:12px;font-weight:600;padding:4px 12px;border-radius:20px;border:1px solid rgba(27,58,107,0.2); }
        .lang-badge { background:#F0F4FF;color:#5B75A8;font-size:11px;font-weight:600;padding:3px 10px;border-radius:20px;border:1px solid #D4DCF0; }
        .messages-area { flex:1;overflow-y:auto;padding:24px 20px;display:flex;flex-direction:column;gap:16px; }
        .messages-area::-webkit-scrollbar{width:4px}
        .messages-area::-webkit-scrollbar-thumb{background:var(--border);border-radius:4px}
        .message-row { display:flex;align-items:flex-end;gap:10px;animation:msgIn 0.25s ease; }
        @keyframes msgIn { from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)} }
        .message-row.user { flex-direction:row-reverse; }
        .avatar-small { width:36px;height:36px;border-radius:50%;flex-shrink:0;margin-bottom:2px;border:2px solid var(--navy-light);object-fit:cover; }
        .bubble { max-width:72%;padding:13px 17px;border-radius:18px;font-size:14.5px;line-height:1.65; }
        .bubble.assistant { background:white;color:var(--text);border-radius:18px 18px 18px 4px;box-shadow:0 2px 12px rgba(0,0,0,0.06);border:1px solid var(--border); }
        .bubble.user { background:linear-gradient(135deg,var(--navy),var(--navy-light));color:white;border-radius:18px 18px 4px 18px;box-shadow:0 4px 16px rgba(27,58,107,0.3); }
        .loading-dots span { animation:blink 1.2s infinite;font-size:22px;color:var(--navy); }
        .loading-dots span:nth-child(2){animation-delay:0.2s}
        .loading-dots span:nth-child(3){animation-delay:0.4s}
        @keyframes blink{0%,80%,100%{opacity:0.2}40%{opacity:1}}
        .input-area { padding:16px 20px;background:white;border-top:1px solid var(--border);display:flex;gap:12px;align-items:center; }
        .input-area input { flex:1;padding:13px 18px;border:2px solid var(--border);border-radius:24px;font-size:15px;font-family:'Noto Sans',sans-serif;background:var(--surface);color:var(--text);outline:none;transition:border-color 0.2s,box-shadow 0.2s; }
        .input-area input:focus { border-color:var(--navy);box-shadow:0 0 0 3px var(--navy-glow);background:white; }
        .input-area input::placeholder{color:#A0AECB}
        .send-btn { width:48px;height:48px;background:linear-gradient(135deg,var(--navy),var(--navy-light));border:none;border-radius:50%;color:white;font-size:18px;cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 14px rgba(27,58,107,0.4);transition:transform 0.15s,box-shadow 0.15s;flex-shrink:0; }
        .send-btn:hover:not(:disabled){transform:scale(1.08);box-shadow:0 6px 20px rgba(27,58,107,0.5)}
        .send-btn:disabled{opacity:0.5;cursor:not-allowed}
        .overlay { display:none;position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:99; }
        @media(max-width:768px){.overlay{display:block}}
      `}</style>

      {page === "landing" && (
        <LandingPage
          onGetStarted={goSignup}
          onLogin={goLogin}
        />
      )}
      {page === "auth" && (
        <OTPAuthPage
          initialMode={authMode}
          onVerified={(sbUser, profileData) => {
            // Signup only — login is routed via onAuthStateChange SIGNED_IN handler
            if (profileData) { setUser(profileData); setPage("home"); }
          }}
          onGoLanding={() => setPage("landing")}
        />
      )}
      {page === "profile-setup" && supabaseUser && (
        <ProfileSetupPage
          supabaseUser={supabaseUser}
          onComplete={(u) => { setUser(u); setPage("home"); }}
        />
      )}
      {page === "home" && user && (
        <HomePage
          user={user}
          onStartChat={() => setPage("chat")}
          onLogout={async () => { await supabase.auth.signOut(); setUser(null); setPage("landing"); }}
        />
      )}
      {page === "chat" && user && (
        <ChatPage
          user={user}
          onLogout={async () => { await supabase.auth.signOut(); setUser(null); setPage("landing"); }}
          onGoHome={() => setPage("home")}
        />
      )}
      {page === "admin" && (
        <AdminPortal onBack={() => setPage("landing")} supabase={supabase} />
      )}
    </>
  );
}
